import { useState } from 'react'
import { View, SafeAreaView, StyleSheet, Text, TouchableOpacity, ImageBackground, Alert, Platform } from 'react-native'
import { Heading, Box, Row, useToast, Divider } from 'native-base'
import { UseCamera } from './UseCamera'
import { useRecoilValue, useRecoilState } from 'recoil'
import { allPhotosState, cameraState, locationState, modalState } from '../../state/atoms'
import { GooglePlacesAutocomplete } from 'react-native-google-places-autocomplete'
import { addDoc, collection, increment, serverTimestamp, updateDoc, doc, query, where, getDocs } from 'firebase/firestore'
import { db, storage, auth } from '../../firebase'
import { getDistance } from 'geolib'
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage'
import { userState } from '../../state/atoms'
import { AddPhoto } from './AddPhoto'
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import { Popover, Button, Modal } from 'native-base'
import LottieView from 'lottie-react-native'

const Initialize = () => {
    const camera = useRecoilValue(cameraState)
    const user = useRecoilValue(userState)
    const currentLocation = useRecoilValue(locationState)
    const [selectedAddressMain, setSelectedAddressMain] = useState('')
    const [selectedAddressDisplay, setSelectedAddressDisplay] = useState('')
    const [addressName, setAddressName] = useState('')
    const [addressReferenceURL, setAddressReferenceURL] = useState('')
    const [addressLatitude, setAddressLatitude] = useState(null)
    const [addressLongitude, setAddressLongitude] = useState(null)
    const [displayAnalytics, setDisplayAnalytics] = useState(false)
    const [userPhotos, setUserPhotos] = useRecoilState(allPhotosState)
    const [showModal, setShowModal] = useRecoilState(modalState)

    const [progress, setProgress] = useState(0)
    const [showInfo, setShowInfo] = useState(false)
    const toast = useToast()
    const homesCollectionRef = collection(db, 'homes')
    const userID = auth.currentUser.uid

    // Conditions
    const addressPresent = selectedAddressMain !== ''
    const minimumOnePhoto = userPhotos.length > 0

    const uploadFiles = (files) => {
        if (!files || files.length === 0) return
        files.forEach(async (file, idx) => {
            const storageRef = ref(storage, `/homes/${addressName}/${file.value}-${idx + 1}`)
            let blob = await fetch(file.image.uri).then((r) => r.blob())
            const uploadTask = uploadBytes(storageRef, blob)
            uploadTask.on(
                'state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                    setProgress(progress)
                },
                (error) => {
                    console.log(error)
                },
                () => {
                    getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                        console.log('File available at', downloadURL)
                    })
                }
            )
        })
    }

    const calculatePoints = () => {
        let submissionPoints = 20
        let photoPoints = 0
        let totalPoints = 0
        if (userPhotos.length > 0) {
            userPhotos.forEach((photo) => {
                photoPoints += photo.points
            })
        }
        totalPoints = submissionPoints + photoPoints
        return totalPoints
    }

    const submitHome = async () => {
        if (!selectedAddressMain) {
            alert('Must add address!')
            return
        }
        try {
            await addDoc(homesCollectionRef, {
                address: selectedAddressMain,
                addressName: addressName,
                addressReferenceURL: addressReferenceURL,
                display_address: selectedAddressDisplay,
                location: { lat: addressLatitude, long: addressLongitude },
                tagged_by: user.username,
                tag_owner_ID: userID,
                claimed: true,
                review_status: 'pending',
                date_tagged: serverTimestamp(),
                potential_points: calculatePoints(),
                points_awarded: 0,
            })
            await updateDoc(doc(db, 'users', userID), {
                'stats.homes_tagged': increment(1),
            })
            await uploadFiles(userPhotos)
        } catch (e) {
            console.error(e)
        }
        setDisplayAnalytics(false)
        setSelectedAddressMain('')
        setUserPhotos([])
        toast.show({ description: 'Home Hunted!', placement: 'top' })
    }

    const analytics = () => {
        return (
            <>
                <Text>
                    Home Location: {addressLatitude}, {addressLongitude}
                </Text>
                <Text>
                    Your Location: {currentLocation?.coords?.latitude},{currentLocation?.coords?.longitude}
                </Text>
                <Divider />
                <Text>
                    You are {distanceInMeters()} meters away. Normally you {distanceInMeters() >= 500 ? 'would NOT' : 'WOULD'} be able to submit this home!
                </Text>
            </>
        )
    }

    const distanceInMeters = () => {
        return getDistance(
            { latitude: addressLatitude, longitude: addressLongitude },
            {
                latitude: currentLocation?.coords?.latitude,
                longitude: currentLocation?.coords?.longitude,
            }
        )
    }

    const handleAddressSelection = async (data, details) => {
        const q = query(collection(db, 'homes'), where('address', '==', details.formatted_address.replace(', USA', '')))
        const querySnapshot = await getDocs(q)
        if (querySnapshot.size > 0) {
            alert('This home has already been tagged!')
        } else {
            setSelectedAddressMain(details.formatted_address.replace(', USA', ''))
            setSelectedAddressDisplay(data.structured_formatting?.main_text)
            setAddressName(data.description)
            setAddressReferenceURL(details.url)
            setAddressLatitude(details.geometry.location.lat)
            setAddressLongitude(details.geometry.location.lng)
            setDisplayAnalytics(true)
        }
    }

    const handleDelete = (index) => {
        const newPhotos = [...userPhotos]
        newPhotos.splice(index, 1)
        setUserPhotos(newPhotos)
    }

    const handleCancelText = () => {
        Alert.alert('Hey there.', 'Are you sure you want to cancel? You will lose all your progress.', [
            {
                text: 'Cancel',
                style: 'cancel',
            },
            {
                text: 'OK',
                onPress: () => {
                    setUserPhotos([])
                    setSelectedAddressMain('')
                },
            },
        ])
    }

    return camera ? (
        <UseCamera />
    ) : (
        <View>
            <ImageBackground source={require('../../assets/background.jpg')} resizeMode="cover" imageStyle={{ opacity: 0.3 }} style={styles.background}>
                {Platform.OS !== 'web' && (
                    <>
                        <LottieView source={require('../../assets/animations/particles.json')} resizeMode="cover" autoPlay loop style={{ opacity: 0.5 }} />
                        <LottieView source={require('../../assets/animations/waves.json')} resizeMode="cover" autoPlay loop style={{ opacity: 0.6 }} />\
                    </>
                )}
                <SafeAreaView style={styles.container}>
                    <View style={styles.header}>
                        <Heading
                            style={{
                                color: '#fff',
                                textShadowColor: 'rgba(0, 0, 0, 1)',
                                textShadowOffset: { width: 1, height: 1 },
                                textShadowRadius: 5,
                            }}
                        >
                            Claim a Zombie Home
                        </Heading>
                        <MaterialCommunityIcons style={{ marginLeft: 5 }} name="information-outline" size={20} color="royalblue" onPress={() => setShowInfo(true)} />
                    </View>
                    <Modal isOpen={showInfo} onClose={() => setShowInfo(false)}>
                        <Modal.Content width={400} padding={5}>
                            <Modal.CloseButton />
                            <Modal.Header marginBottom={5}>How it works</Modal.Header>
                            <Text style={styles.description}>• Enter an address below and select the applicable location. </Text>
                            <Text style={styles.description}>• You must be within 500 feet of the home in order to submit. </Text>
                            <Text style={styles.description}>• This is done to prevent people from mass submitting homes. </Text>
                            <Text style={styles.description}>• Add photos of the home, and tag various conditions with photo proof for additional points. </Text>
                            <Text style={styles.description}>• When done, hit submit. Once reviewed, you will be notified and the home will show in your account as claimed.</Text>
                        </Modal.Content>
                    </Modal>
                    <Row space={4} w="90%" mx="auto">
                        {selectedAddressMain ? (
                            <View style={styles.claimedDisplay}>
                                <Text style={{ color: '#fff' }}>You are claiming:</Text>
                                <Heading style={styles.selectedAddress}>{selectedAddressMain}</Heading>
                                <Text style={styles.cancelText} onPress={() => handleCancelText()}>
                                    Clear location?
                                </Text>
                            </View>
                        ) : (
                            <GooglePlacesAutocomplete
                                placeholder="Enter address to start claiming!"
                                onPress={(data, details = null) => {
                                    handleAddressSelection(data, details)
                                }}
                                fetchDetails={true}
                                query={{
                                    key: 'AIzaSyCp0jm7KsWOSW-uiZN_um4qs3GYnXUfKkA',
                                    language: 'en',
                                    components: 'country:us',
                                }}
                            />
                        )}
                    </Row>
                    {currentLocation?.coords ? displayAnalytics && analytics() : <Text>Loading your current location...</Text>}
                    {selectedAddressMain && (
                        <View>
                            {showModal ? (
                                <Modal isOpen={showModal} onClose={() => setShowModal(false)} size="lg">
                                    <Modal.Content maxWidth={350} padding={30}>
                                        <Modal.CloseButton />
                                        <AddPhoto />
                                    </Modal.Content>
                                </Modal>
                            ) : (
                                <TouchableOpacity style={styles.button} onPress={() => setShowModal(true)}>
                                    <Text style={styles.buttonText}>{userPhotos.length > 0 ? 'Add another photo' : 'Add a photo'}</Text>
                                </TouchableOpacity>
                            )}
                        </View>
                    )}
                    <View style={styles.photoSection}>
                        {userPhotos.map((photo, index) => (
                            <Box key={index} style={styles.photoBox} onPress={handleDelete}>
                                <Popover
                                    trigger={(triggerProps) => {
                                        return (
                                            <TouchableOpacity {...triggerProps}>
                                                <ImageBackground
                                                    key={index}
                                                    source={{ uri: photo.image.uri }}
                                                    style={{
                                                        width: 80,
                                                        height: 80,
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                    resizeMode="cover"
                                                >
                                                    <MaterialCommunityIcons
                                                        name={photo.icon}
                                                        size={48}
                                                        color={'white'}
                                                        style={{
                                                            textShadowColor: 'rgba(0, 0, 0, 1)',
                                                            textShadowOffset: { width: 1, height: 1 },
                                                            textShadowRadius: 10,
                                                        }}
                                                    />
                                                </ImageBackground>
                                            </TouchableOpacity>
                                        )
                                    }}
                                >
                                    <Popover.Content accessibilityLabel="Delete photo" w="56">
                                        <Popover.Arrow />
                                        <Popover.CloseButton />
                                        <Popover.Header>Delete photo</Popover.Header>
                                        <Popover.Body>This will remove the photo. Deleted photos can not be recovered. Please ensure you have local copy of photos.</Popover.Body>
                                        <Popover.Footer justifyContent="flex-end">
                                            <Button style={{ width: '100%' }} colorScheme="danger" onPress={handleDelete}>
                                                Delete
                                            </Button>
                                        </Popover.Footer>
                                    </Popover.Content>
                                </Popover>
                            </Box>
                        ))}
                    </View>
                    <View>
                        {addressPresent && minimumOnePhoto && (
                            <TouchableOpacity style={styles.button} onPress={() => submitHome()}>
                                <Text style={styles.buttonText}>Submit Home</Text>
                            </TouchableOpacity>
                        )}
                    </View>
                </SafeAreaView>
            </ImageBackground>
        </View>
    )
}

export default Initialize

const styles = StyleSheet.create({
    container: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        marginBottom: 20,
    },
    background: {
        height: '100%',
    },
    cancelText: {
        color: '#b00020',
        fontWeight: 'bold',
        fontSize: 12,
        marginBottom: 10,
    },
    photoSection: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        marginTop: 20,
        overflow: 'scroll',
        maxHeight: 300,
    },
    photoBox: {
        width: 80,
        height: 80,
        margin: 10,
        border: '1px solid grey',
        borderRadius: 10,
        overflow: 'hidden',
    },
    selectedAddress: {
        fontSize: 16,
    },
    button: {
        backgroundColor: '#6c387a',
        width: '60%',
        padding: 15,
        borderRadius: 5,
        alignItems: 'center',
        marginTop: 20,
    },
    buttonText: {
        color: 'white',
        fontWeight: '700',
        fontSize: 16,
    },
    description: {
        fontSize: 12,
    },
    claimedDisplay: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    selectedAddress: {
        fontSize: 18,
    },
})
